import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import {
  isPlaceholderName,
  mergeSpeakers,
  speakerStats,
  splitSpeaker,
  updateRole,
} from '../../analysis/draft'
import type { AnalysisDraft, DraftRole, SpeakerStats } from '../../analysis/draft'
import { formatTime } from '../../lib/format'

export type SpeakerRolesProps = {
  draft: AnalysisDraft
  disabled: boolean
  onChange: (next: AnalysisDraft) => void
  onSeek: (time: number) => void
}

type RoleRowProps = {
  role: DraftRole
  stats: SpeakerStats
  others: DraftRole[]
  disabled: boolean
  onPatch: (speaker: string, patch: Partial<Omit<DraftRole, 'speaker'>>) => void
  onMerge: (from: string, into: string) => void
  onSplit: (speaker: string) => void
  onSeek: (time: number) => void
}

function SpeakerRow({
  role,
  stats,
  others,
  disabled,
  onPatch,
  onMerge,
  onSplit,
  onSeek,
}: RoleRowProps): JSX.Element {
  const [name, setName] = useState(role.name)
  useEffect(() => setName(role.name), [role.name])

  const commitName = (): void => {
    const trimmed = name.trim()
    if (trimmed === role.name) return
    if (!trimmed) {
      setName(role.name)
      return
    }
    onPatch(role.speaker, { name: trimmed })
  }

  const first = stats.first
  const merged = stats.merged.length > 1

  return (
    <div className="speaker-row" style={{ borderLeftColor: role.color }}>
      <div className="row wrap">
        <input
          type="color"
          value={role.color}
          disabled={disabled}
          title="Цвет роли"
          aria-label={`Цвет роли ${role.name}`}
          onChange={(e) => onPatch(role.speaker, { color: e.currentTarget.value })}
        />
        <input
          type="text"
          value={name}
          disabled={disabled}
          placeholder="Имя персонажа"
          aria-label={`Имя роли для ${role.speaker}`}
          style={{ maxWidth: 200 }}
          onChange={(e) => setName(e.currentTarget.value)}
          onBlur={commitName}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur()
            if (e.key === 'Escape') setName(role.name)
          }}
        />
        <span className="badge mono" title="Метка диаризации">
          {role.speaker || 'без метки'}
        </span>
        {isPlaceholderName(name) ? (
          <span className="badge" style={{ color: 'var(--warning)' }}>
            имя не задано
          </span>
        ) : null}
        <span className="spacer" />
        <span className="faint mono">
          {stats.lines} репл. · {formatTime(stats.speech)} речи
        </span>
      </div>

      <div className="row wrap speaker-first">
        {first ? (
          <>
            <button
              type="button"
              className="ghost small mono"
              onClick={() => onSeek(first.start)}
              title="Перемотать видео к первой реплике"
            >
              ▶ {formatTime(first.start)}
            </button>
            <span className="faint" style={{ minWidth: 0 }}>
              {first.textOriginal || first.text || 'реплика без текста'}
            </span>
          </>
        ) : (
          <span className="faint">Реплик не осталось — роль не будет создана.</span>
        )}
      </div>

      <div className="row wrap">
        {merged ? (
          <>
            <span className="badge" title="Объединённые метки диаризации">
              объединено: {stats.merged.join(', ')}
            </span>
            <button
              type="button"
              className="ghost small"
              disabled={disabled}
              onClick={() => onSplit(role.speaker)}
            >
              Разъединить
            </button>
          </>
        ) : null}
        {others.length > 0 ? (
          <label className="row check" style={{ margin: 0 }}>
            <span className="faint">Объединить с</span>
            <select
              value=""
              disabled={disabled}
              aria-label={`Объединить роль ${role.name} с другой`}
              style={{ width: 'auto', minWidth: 140 }}
              onChange={(e) => {
                const target = e.currentTarget.value
                if (target) onMerge(role.speaker, target)
              }}
            >
              <option value="">— выбрать —</option>
              {others.map((other) => (
                <option key={other.speaker} value={other.speaker}>
                  {other.name} ({other.speaker})
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    </div>
  )
}

export function SpeakerRoles({ draft, disabled, onChange, onSeek }: SpeakerRolesProps): JSX.Element {
  const stats = useMemo(
    () => new Map(draft.roles.map((role) => [role.speaker, speakerStats(draft.lines, role.speaker)])),
    [draft.roles, draft.lines],
  )
  const orphans = useMemo(() => draft.lines.filter((line) => !line.speaker).length, [draft.lines])
  const unnamed = draft.roles.filter((role) => isPlaceholderName(role.name)).length

  return (
    <div className="stack">
      <div className="row wrap">
        <h3 style={{ margin: 0 }}>Кто говорит</h3>
        <span className="faint">{draft.roles.length}</span>
        <span className="spacer" />
        {draft.diarizer ? <span className="badge mono">{draft.diarizer}</span> : null}
      </div>

      <p className="faint" style={{ margin: 0 }}>
        «SPEAKER_00» — не имя, а метка. Опознать персонажа проще всего по первой реплике:
        включите её и подпишите роль. Диаризация часто дробит один голос на несколько меток —
        такие роли объединяйте.
      </p>

      {draft.roles.length === 0 ? (
        <p className="notice warn" style={{ margin: 0 }}>
          Говорящих не нашлось. Все реплики останутся без роли — назначите их вручную после
          применения.
        </p>
      ) : (
        draft.roles.map((role) => (
          <SpeakerRow
            key={role.speaker}
            role={role}
            stats={stats.get(role.speaker) ?? speakerStats(draft.lines, role.speaker)}
            others={draft.roles.filter((other) => other.speaker !== role.speaker)}
            disabled={disabled}
            onPatch={(speaker, patch) => onChange(updateRole(draft, speaker, patch))}
            onMerge={(from, into) => onChange(mergeSpeakers(draft, from, into))}
            onSplit={(speaker) => onChange(splitSpeaker(draft, speaker))}
            onSeek={onSeek}
          />
        ))
      )}

      {unnamed > 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          Без имени осталось ролей: {unnamed}. Их создадут как «Говорящий N» — переименовать
          можно и потом, в панели ролей.
        </p>
      ) : null}

      {orphans > 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          Реплик без говорящего: {orphans}. Сшивка не нашла им пары и не стала угадывать —
          роль можно проставить в таблице ниже.
        </p>
      ) : null}
    </div>
  )
}
